import React, { useEffect, useRef, useState } from "react";
import {
  webLoadingFalse,
  webNavigate,
} from "../../services/navigate.service";
import PlusIcon from "../../images/PlusIcon";
import CloseIcon from "../../images/CloseIcon";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import {
  extractValues,
  updateUrlParams,
  validateNoSpecialChars,
} from "../../validation/ValidationInput";
import UploadIcon from "../../images/UploadIcon";
import InformationIcon from "../../images/InformationIcon";
import {
  createLanguage,
  createTranslation,
  deleteLanguage,
  deleteTranslation,
  loadDetailLanguage,
  loadDetailTranslation,
  loadLanguage,
  loadListKey,
  loadTranslation,
  updatdeLanguage,
  updatdeTranslation,
} from "../../services/language.services";
import PopupLocation from "../../components/PopupLocation";
import { showApiError } from "../../services/apiError";
import { useResponsive } from "../../hook/useResponsive";
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
} from "../../components/list";
import { ConfirmSheet } from "../../components/mobile";
interface LanguageRow {
  id: string;
  code: string;
  name: string;
  icon: string;
}
interface TranslationRow {
  id: string;
  value: string;
  key?: { key: string };
  language?: { name: string };
}

const Languages = () => {
  const [listKey, setListKey] = useState<any>([]);
  const [language, setLanguage] = useState<any>(null);
  const [listLanguages, setListLanguages] = useState<any>([]);
  const { isTouchLayout } = useResponsive();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const [id, setId] = useState<string>("");
  const [showPopup, setShowPopup] = useState<boolean>(false);
  // Read before the list mounts so `key={tab}` does not remount it on load.
  const [tab, setTab] = useState<string>(() => {
    const filterParams: any = extractValues(location.search.split("&"));
    return filterParams.tab === "Translations" ? "Translations" : "Languages";
  });
  const [errMessage, setErrMessage] = useState<any>("");
  const [errors, setErrors] = useState<any>({});
  const [formValues, setFormValues] = useState<any>({});
  const userId: string = localStorage.getItem("userId") || "";
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [showCreate, setShowCreate] = useState<any>({
    show: false,
    isNew: true,
  });
  const [idDelete, setIdDelete] = useState<string>("");
  const [fileName, setFileName] = useState("");
  const [base64Image, setBase64Image] = useState<any>("");
  const dropdownKeyref = useRef<HTMLDivElement>(null);
  const dropdownLanguageref = useRef<HTMLDivElement>(null);
  const [keyTranslation, setKeyTranslation] = useState("");
  const [languageSearch, setLanguageSearch] = useState("");
  const [isKeyTranslationDropdownOpen, setIsKeyTranslationDropdownOpen] =
    useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const preLanguage = localStorage.getItem("language");
  const emptyState = {
    title:
      language && language?.[`No ${tab} Found`]
        ? language?.[`No ${tab} Found`]
        : `No ${tab} Found`,
    desc:
      language && language?.["A short description about this."]
        ? language?.["A short description about this."]
        : "A short description about this.",
  };
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);
  // Back / Forward on this page: re-read ?tab; `key={tab}` remounts the list.
  useEffect(() => {
    const path = location.pathname;
    const handlePopState = () => {
      if (location.pathname !== path) return;
      const filterParams: any = extractValues(location.search.split("&"));
      setTab(filterParams.tab === "Translations" ? "Translations" : "Languages");
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);
  useEffect(() => {
    getListKey();
    const handleNewToken = () => {
      getListKey();
    };
    document.addEventListener("newToken", handleNewToken);
    return () => {
      document.removeEventListener("newToken", handleNewToken);
    };
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownKeyref.current &&
        !dropdownKeyref.current.contains(event.target as Node)
      ) {
        setIsKeyTranslationDropdownOpen(false);
      }
      if (
        dropdownLanguageref.current &&
        !dropdownLanguageref.current.contains(event.target as Node)
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    const { name, value, type } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f") {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const getListKey = async () => {
    const response: any = await loadListKey();
    const keyArray = response?.data?.map((each: any) => each.key) || [];
    const languageArray =
      response?.data
        ?.map((each: any) => each.language)
        .filter(
          (value: any, index: any, self: any) =>
            self.findIndex((lang: any) => lang.name === value.name) === index,
        ) || [];
    setListLanguages(languageArray);
    setListKey(keyArray);
  };

  /** One page of the active tab; both endpoints take `searchValue` (languages: code/name, translations: value). */
  const fetchTabRows = async (query: ListQuery) => {
    const searchValue = query.search.trim();
    const response: any =
      tab === "Languages"
        ? await loadLanguage(query.page, query.limit, searchValue)
        : await loadTranslation(query.page, query.limit, searchValue);
    return {
      rows: response?.data?.data ?? [],
      count: response?.data?.count ?? 0,
    };
  };

  const goToRow = (rowId: string) =>
    webNavigate(
      "/" + preLanguage + (tab === "Languages" ? "/languages/" : "/translation/") + rowId,
    );

  const handleShowDelete = (id: string) => {
    setShowDelete(true);
    setIdDelete(id);
  };

  const handleShowCreate = async (id: string) => {
    setShowCreate({ isNew: false, show: true });
    setId(id);
    if (tab === "Languages") {
      const response: any = await loadDetailLanguage(id);
      setFormValues(response.data);
      setFileName(response.data.icon);
      setBase64Image(response.data.icon);
    } else {
      const response: any = await loadDetailTranslation(id);
      setFormValues(response.data);
      setKeyTranslation(response.data?.key?.key);
      setLanguageSearch(response.data?.language?.name);
    }
  };

  const handleKeySelect = (keyId: string, keyName: string) => {
    setFormValues((prev: any) => ({ ...prev, key: keyName }));
    setKeyTranslation(keyName);
    setIsKeyTranslationDropdownOpen(false);
  };
  const handleLanguageSelect = (languageId: string, name: string) => {
    setFormValues((prev: any) => ({ ...prev, languageId: languageId }));
    setLanguageSearch(name);
    setIsLanguageDropdownOpen(false);
  };
  const handleDeleteEmployee = async () => {
    try {
      if (tab === "Languages") {
        await deleteLanguage(idDelete);
      } else {
        await deleteTranslation(idDelete);
      }
    } catch (error) {
      showApiError(error, { language });
    } finally {
      listRef.current?.reload();
      setShowDelete(false);
    }
  };

  const handleSavePopup = async () => {
    if (tab === "Languages") {
      try {
        const payload: any = {
          ...formValues,
          icon: base64Image,
          userId,
        };
        if (id !== "") {
          const response: any = await updatdeLanguage(id, payload);
        } else {
          const response: any = await createLanguage(payload);
        }
        webLoadingFalse();
      } catch (error: any) {
        setErrMessage(showApiError(error, { language }).message);
        setShowPopup(true);
        webLoadingFalse();
      }
    } else {
      try {
        const payload: any = {
          ...formValues,
          userId,
        };
        if (id !== "") {
          const response: any = await updatdeTranslation(id, payload);
        } else {
          const response: any = await createTranslation(payload);
        }
        webLoadingFalse();
      } catch (error: any) {
        setErrMessage(showApiError(error, { language }).message);
        setShowPopup(true);
        webLoadingFalse();
      }
    }
    setShowCreate({ isNew: true, show: false });
    setFormValues({
      code: "",
      name: "",
      value: "",
      keyId: "",
    });
    setKeyTranslation("");
    setLanguageSearch("");
    setErrors({});
    setBase64Image({});
    setFileName("");
  };

  const handleCancelPopup = () => {
    setShowCreate({ isNew: true, show: false });
    setFormValues({
      code: "",
      name: "",
      value: "",
      keyId: "",
    });
    setKeyTranslation("");
    setErrors({});
    setBase64Image({});
    setFileName("");
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Image = reader.result;
        setFileName(file.name);
        setBase64Image(base64Image);
      };
      reader.readAsDataURL(file);
    } else {
      alert("Please upload an image file.");
    }
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64 = reader.result as string;
        setFileName(file.name);
        setBase64Image(base64);
      };

      reader.readAsDataURL(file);
    } else {
      alert("Please upload a valid image file.");
    }
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleClosePopup = () => {
    setShowPopup(false);
  };
  const handleOpenPopup = () => {
    setShowPopup(true);
  };

  /** Switching tabs resets the list (page 1, no search); `key={tab}` remounts it so it re-reads the URL. */
  const handleChangeTab = (value: string) => {
    if (value === tab) return;
    setTab(value);
    webNavigate(updateUrlParams({ tab: value, page: 1, limit: 50 }));
  };

  const languageColumns: ListColumn<LanguageRow>[] = [
    {
      key: "name",
      label: "Name",
      width: 240,
      hideable: false,
      render: cells.primary((r) => r.name),
      value: (r) => r.name ?? "",
      mobile: "title",
      onClick: (r) => goToRow(String(r.id)),
    },
    { key: "code", label: "Code", width: 140, value: (r) => r.code ?? "", mobile: "subtitle" },
    {
      key: "icon",
      label: "Icon",
      width: 96,
      render: (r) =>
        r.icon ? (
          <img
            src={String(r.icon)}
            alt=""
            style={{ height: "24px", width: "24px", objectFit: "contain" }}
          />
        ) : null,
      mobile: "trailing",
    },
  ];

  const translationColumns: ListColumn<TranslationRow>[] = [
    {
      key: "key",
      label: "Key",
      width: 260,
      hideable: false,
      render: cells.primary((r) => r.key?.key ?? ""),
      value: (r) => r.key?.key ?? "",
      mobile: "title",
      onClick: (r) => goToRow(String(r.id)),
    },
    {
      key: "language",
      label: "Language",
      width: 160,
      value: (r) => r.language?.name ?? "",
      mobile: "trailing",
    },
    {
      key: "value",
      label: "Translation",
      width: 360,
      value: (r) => r.value ?? "",
      mobile: "subtitle",
    },
  ];

  const rowActions: ListRowAction[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditIconV2 />,
      sheetIcon: "chev",
      onRun: (r) => goToRow(String(r.id)),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteIconV2 />,
      sheetIcon: "close",
      danger: true,
      onRun: (r) => handleShowDelete(String(r.id)),
    },
  ];

  const deleteTitle =
    language && language?.["Delete Item?"] ? language?.["Delete Item?"] : "Delete Item?";
  const deleteMessage =
    language && language?.[`You will permanently delete ${tab}`]
      ? language?.[`You will permanently delete ${tab}`]
      : `You will permanently delete ${tab}`;
  const cancelLabel = language && language?.["Cancel"] ? language?.["Cancel"] : "Cancel";
  const deleteLabel = language && language?.["Delete"] ? language?.["Delete"] : "Delete";

  return (
    <>
      <div
        className="h-full relative"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflowY: isTouchLayout ? "auto" : undefined,
          paddingBottom: isTouchLayout ? "24px" : undefined,
        }}
      >
        <div style={{ flex: "none" }}>
          <p
            style={{ color: "#222525" }}
            className="text-[20px] leading-[130%] font-medium font-inter"
          >
            {language && language?.["Languages"]
              ? language?.["Languages"]
              : "Languages"}
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              marginTop: "12px",
            }}
          >
            <div className="flex item-cemter" style={{ gap: "12px" }}>
              <div
                style={{
                  color: tab === "Languages" ? "#FFFFFF" : "#6A7878",
                  padding: "6px 14px",
                  borderRadius: "30px",
                  backgroundColor:
                    tab === "Languages" ? "#0D5759" : "transparent",
                  border:
                    tab === "Languages"
                      ? "1px solid transparent"
                      : "1px solid #C9D7D8",
                }}
                onClick={() => handleChangeTab("Languages")}
                className="cursor-pointer text-[14px] leading-[130%] font-medium font-inter"
              >
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "21px",
                    color: tab === "Languages" ? "#FFFFFF" : "#6A7878",
                  }}
                >
                  {language && language?.["Languages"]
                    ? language?.["Languages"]
                    : "Languages"}
                </p>
              </div>
              <div
                style={{
                  color: tab === "Translations" ? "#FFFFFF" : "#6A7878",
                  padding: "6px 14px",
                  borderRadius: "30px",
                  backgroundColor:
                    tab === "Translations" ? "#0D5759" : "transparent",
                  border:
                    tab === "Translations"
                      ? "1px solid transparent"
                      : "1px solid #C9D7D8",
                }}
                onClick={() => handleChangeTab("Translations")}
                className="cursor-pointer text-[14px] leading-[130%] font-medium font-inter"
              >
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "21px",
                    color: tab === "Translations" ? "#FFFFFF" : "#6A7878",
                  }}
                >
                  {language && language?.["Translations"]
                    ? language?.["Translations"]
                    : "Translations"}
                </p>
              </div>
            </div>
            <div
              className="cursor-pointer flex items-center justify-center"
              style={{
                gap: "8px",
                padding: "8px 24px 8px 16px",
                backgroundColor: "#0F7679",
                borderRadius: "30px",
                width: "fit-content",
              }}
              onClick={() => {
                tab === "Languages"
                  ? webNavigate("/" + preLanguage + "/languages/new")
                  : webNavigate("/" + preLanguage + "/translation/new");
              }}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <PlusIcon />
              </div>
              {tab === "Languages" ? (
                <p
                  style={{ color: "#FFFFFF" }}
                  className="text-[14px] leading-[130%] font-medium font-inter"
                >
                  {language && language?.["Add languages"]
                    ? language?.["Add languages"]
                    : "Add languages"}
                </p>
              ) : (
                <p
                  style={{ color: "#FFFFFF" }}
                  className="text-[14px] leading-[130%] font-medium font-inter"
                >
                  {language && language?.["Add translations"]
                    ? language?.["Add translations"]
                    : "Add translations"}
                </p>
              )}
            </div>
          </div>
        </div>
        <ListScreen
          key={tab}
          ref={listRef}
          language={language}
          columns={tab === "Languages" ? languageColumns : translationColumns}
          fetchRows={fetchTabRows}
          searchPlaceholder={tab === "Languages" ? "Search languages" : "Search translations"}
          searchInputRef={searchInputRef}
          rowActions={rowActions}
          countNoun={tab === "Languages" ? "languages" : "translations"}
          emptyState={emptyState}
          extraUrlParams={{ tab }}
        />
        <div
          className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showCreate.show ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
          style={{
            backgroundColor: "#22252552",
            zIndex: "200",
            transition: "opacity 0.3s ease, visibility 0.3s ease",
            width: "100%",
          }}
        >
          <div
            className={`transform transition-transform duration-300 ease-in-out ${showCreate.show ? "translate-y-0" : "translate-y-4 opacity-0"}`}
            style={{
              borderRadius: "6px",
              backgroundColor: "white",
              width: "400px",
              maxWidth: "calc(100vw - 32px)",
              transition: "transform 0.3s ease, opacity 0.3s ease",
            }}
          >
            <div className="flex items-center justify-between">
              <p className="text-[16px] font-semibold font-inter text-main leading-[24px]">
                {language &&
                  language?.[`${showCreate.isNew ? "New" : "Update"} ${tab}`]
                  ? language?.[`${showCreate.isNew ? "New" : "Update"} ${tab}`]
                  : `${showCreate.isNew ? "New" : "Update"} ${tab}`}
              </p>
              <div
                onClick={() => setShowCreate({ ...showCreate, show: false })}
                className="flex items-center justify-center cursor-pointer"
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "100%",
                  backgroundColor: "#D1E4E5",
                }}
              >
                <CloseIcon />
              </div>
            </div>
            <div className="" style={{ padding: "20px 16px" }}>
              {tab === "Languages" ? (
                <div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p
                        className="text-[14px] font-inter leading-[20px]"
                        style={{ color: "#2D2D2D" }}
                      >
                        {language && language?.["Code"]
                          ? language?.["Code"]
                          : "Code"}
                      </p>
                    </div>
                    <div className="w-full">
                      <input
                        tabIndex={1}
                        value={formValues?.code}
                        onKeyDown={validateNoSpecialChars}
                        name="code"
                        onChange={(e: any) => {
                          handleChange(e);
                        }}
                        className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                      />
                    </div>
                    {errors.code && <p className="error">{errors.code}</p>}
                  </div>
                  <div className="space-y-2" style={{ marginTop: "24px" }}>
                    <div className="flex items-center justify-between">
                      <p
                        className="text-[14px] font-inter leading-[20px]"
                        style={{ color: "#2D2D2D" }}
                      >
                        {language && language?.["Name"]
                          ? language?.["Name"]
                          : "Name"}
                      </p>
                    </div>
                    <div className="w-full">
                      <input
                        tabIndex={2}
                        value={formValues?.name}
                        onKeyDown={validateNoSpecialChars}
                        name="name"
                        onChange={(e: any) => {
                          handleChange(e);
                        }}
                        className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                      />
                    </div>
                    {errors.name && <p className="error">{errors.name}</p>}
                  </div>
                  <div style={{ marginTop: "24px" }}>
                    <div className="flex items-center justify-between">
                      <p
                        className="text-[14px] font-inter leading-[20px]"
                        style={{ color: "#2D2D2D" }}
                      >
                        {language && language?.["Icon"]
                          ? language?.["Icon"]
                          : "Icon"}
                      </p>
                    </div>
                    <div
                      className="w-full flex items-center justify-center"
                      style={{
                        marginTop: "6px",
                        padding: "40px 12px",
                        backgroundColor: "#FCFDFD",
                        border: `1px dashed ${isDragging ? "#3AC0C4" : "#B8D5D5"}`,
                        borderRadius: "4px",
                      }}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      <div className="space-y-4 text-center">
                        <div className="flex items-center justify-center">
                          <UploadIcon />
                        </div>
                        <label
                          htmlFor="fileInput"
                          className="text-[14px] font-inter  font-semibold leading-[18px] cursor-pointer"
                          style={{
                            color: "#3AC0C4",
                            textDecoration: "underline",
                            marginTop: "16px",
                          }}
                        >
                          {language && language?.["Upload Icon"]
                            ? language?.["Upload Icon"]
                            : "Upload Icon"}
                        </label>
                        <p className="text-[14px] text-main leading-[20px] font-inter">
                          {fileName
                            ? `File: ${fileName}`
                            : language && language?.["or drag & drop here"]
                              ? language?.["or drag & drop here"]
                              : "or drag & drop here"}
                        </p>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                          id="fileInput"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className="p-3 flex items-center gap-x-2"
                    style={{
                      marginTop: "12px",
                      backgroundColor: "#E5F0FE",
                      borderRadius: "4px",
                    }}
                  >
                    <InformationIcon />
                    <p className="text-[12px] font-inter leading-[16px] font-semibold text-subtitle">
                      {language && language?.["Supported files are JPG or PNG."]
                        ? language?.["Supported files are JPG or PNG."]
                        : "Supported files are JPG or PNG."}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p
                        className="text-[14px] font-inter leading-[20px]"
                        style={{ color: "#4A5353" }}
                      >
                        {language && language?.["Key"]
                          ? language?.["Key"]
                          : "Key"}
                      </p>
                    </div>
                    <div className="w-full">
                      <div className="relative" ref={dropdownKeyref}>
                        <input
                          type="text"
                          onKeyDown={validateNoSpecialChars}
                          tabIndex={1}
                          value={keyTranslation}
                          onChange={(e) => {
                            setKeyTranslation(e.target.value);
                            setIsKeyTranslationDropdownOpen(true);
                          }}
                          onClick={() => setIsKeyTranslationDropdownOpen(true)}
                          className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                        />

                        {isKeyTranslationDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-[#EBEBED] rounded-lg max-h-60 overflow-auto p-1 shadow-extend">
                            {listKey.map((key: any) => (
                              <div
                                key={key.id}
                                className="px-4 py-2 cursor-pointer hover:bg-[#E7F1F1] text-main"
                                onClick={() => handleKeySelect(key.id, key.key)}
                              >
                                {key.key}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2" style={{ marginTop: "24px" }}>
                    <div className="flex items-center justify-between">
                      <p
                        className="text-[14px] font-inter leading-[20px]"
                        style={{ color: "#4A5353" }}
                      >
                        {language && language?.["Language"]
                          ? language?.["Language"]
                          : "Language"}
                      </p>
                    </div>
                    <div className="w-full">
                      <div className="relative" ref={dropdownLanguageref}>
                        <input
                          type="text"
                          onKeyDown={validateNoSpecialChars}
                          tabIndex={2}
                          value={languageSearch}
                          onChange={(e) => {
                            setLanguageSearch(e.target.value);
                            setIsLanguageDropdownOpen(true);
                          }}
                          onClick={() => setIsLanguageDropdownOpen(true)}
                          className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                        />

                        {isLanguageDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-[#EBEBED] rounded-lg max-h-60 overflow-auto p-1 shadow-extend">
                            {listLanguages.map((language: any) => (
                              <div
                                key={language.id}
                                className="px-4 py-2 cursor-pointer hover:bg-[#E7F1F1] text-main"
                                onClick={() =>
                                  handleLanguageSelect(
                                    language.id,
                                    language.name,
                                  )
                                }
                              >
                                {language.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2" style={{ marginTop: "24px" }}>
                    <div className="flex items-center justify-between">
                      <p
                        className="text-[14px] font-inter leading-[20px]"
                        style={{ color: "#2D2D2D" }}
                      >
                        {language && language?.["Translation"]
                          ? language?.["Translation"]
                          : "Translation"}
                      </p>
                    </div>
                    <div className="w-full">
                      <input
                        tabIndex={3}
                        value={formValues?.value}
                        onKeyDown={validateNoSpecialChars}
                        name="value"
                        onChange={(e: any) => {
                          handleChange(e);
                        }}
                        className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                      />
                    </div>
                    {errors.value && <p className="error">{errors.value}</p>}
                  </div>
                </>
              )}
            </div>
            <div
              className="flex items-center gap-x-3"
              style={{ padding: "0px 16px 12px 16px" }}
            >
              <div
                onClick={() => handleCancelPopup()}
                className="cursor-pointer w-1/2 text-[14px] leading-[22px] font-inter flex items-center justify-center"
                style={{
                  borderRadius: "6px",
                  height: "40px",
                  border: "1px solid #DEDEE2",
                  color: "#1E1E36",
                }}
              >
                {language && language?.["Cancel"]
                  ? language?.["Cancel"]
                  : "Cancel"}
              </div>
              <div
                onClick={handleSavePopup}
                className="cursor-pointer w-1/2 text-[14px] leading-[22px] font-inter flex items-center justify-center font-medium"
                style={{
                  borderRadius: "6px",
                  height: "40px",
                  backgroundColor: "#3AC0C4",
                  color: "#2D2D2D",
                }}
              >
                {language && language?.["Save"] ? language?.["Save"] : "Save"}
              </div>
            </div>
          </div>
        </div>
      </div>
      {isTouchLayout ? (
        <ConfirmSheet
          open={showDelete}
          title={deleteTitle}
          message={deleteMessage}
          confirmLabel={deleteLabel}
          cancelLabel={cancelLabel}
          onConfirm={handleDeleteEmployee}
          onCancel={() => setShowDelete(false)}
        />
      ) : (
        <div
          className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
          style={{
            backgroundColor: "#22252552",
            zIndex: "200",
            transition: "opacity 0.3s ease, visibility 0.3s ease",
            width: "100vw",
            height: "100vh",
            left: 0,
            top: 0,
          }}
        >
          <div
            className={`transform transition-transform duration-300 ease-in-out ${showDelete ? "translate-y-0" : "translate-y-4 opacity-0"}`}
            style={{
              padding: "24px",
              borderRadius: "24px",
              backgroundColor: "#FFFFFF",
              width: "530px",
              maxWidth: "calc(100vw - 32px)",
              transition: "transform 0.3s ease, opacity 0.3s ease",
            }}
          >
            <div className="flex items-center justify-between">
              <p
                style={{ fontSize: "20px", color: "#222525" }}
                className="font-semibold font-inter leading-[24px]"
              >
                {deleteTitle}
              </p>
              <div
                onClick={() => setShowDelete(false)}
                className="flex items-center justify-center cursor-pointer"
                style={{
                  width: "24px",
                  height: "24px",
                }}
              >
                <CloseIcon />
              </div>
            </div>
            <div
              className="flex items-center justify-between"
              style={{ marginTop: "12px" }}
            >
              <p
                className="text-[14px]  font-inter leading-[24px]"
                style={{ color: "#6A7878" }}
              >
                {deleteMessage}
              </p>
            </div>
            <div
              className="flex items-center justify-end"
              style={{ marginTop: "24px", gap: "12px" }}
            >
              <div
                onClick={() => setShowDelete(false)}
                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                style={{
                  borderRadius: "40px",
                  padding: "10px 24px",
                  border: "1px solid #C9D7D8",
                  color: "#222525",
                }}
              >
                {cancelLabel}
              </div>
              <div
                onClick={handleDeleteEmployee}
                className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                style={{
                  borderRadius: "40px",
                  padding: "10px 24px",
                  backgroundColor: "#BA0E14",
                  color: "#FFFFFF",
                }}
              >
                {deleteLabel}
              </div>
            </div>
          </div>
        </div>
      )}
      <PopupLocation
        show={showPopup}
        title={tab}
        desc={errMessage}
        onClose={handleClosePopup}
        onOpen={handleOpenPopup}
      />
    </>
  );
};

export default Languages;
